/**
 * Theme v2 — canonical DS artifact.
 * See docs/design-system/theme-v2-design-2026-04-19.md
 */

export interface ThemeV2 {
  $id: string;                // cogo:Theme/<slug>
  version: '2.0.0';
  metadata?: {
    name?: string;
    description?: string;
    createdAt?: string;
    provenance?: {
      axes?: Record<string, string>;
      figmaFile?: string;
    };
  };
  primitives: Primitives;
  atoms: Record<string, AtomDefaults>;
  narrative?: Narrative;
  i18n?: I18nConfig;
  icons?: { library?: string; custom?: Record<string, string> };
}

export interface Primitives {
  colors: { light: ColorSet; dark?: ColorSet };
  typography: Typography;
  spacing: Spacing;
  radius: { base: string };
  elevation: Elevation;
  motion: Motion;
}

export interface ColorSet {
  background: string;
  foreground: string;
  primary: string;
  'primary-fg'?: string;
  secondary?: string;
  'secondary-fg'?: string;
  muted?: string;
  accent?: string;
  destructive?: string;
  border?: string;
  input?: string;
  ring?: string;
  card?: string;
  'card-fg'?: string;
  popover?: string;
  'chart-1'?: string;
  'chart-2'?: string;
  'chart-3'?: string;
  'chart-4'?: string;
  'chart-5'?: string;
}

export interface Typography {
  families: { sans: string; mono: string; heading: string };
  scale: Record<string, { size: string; lineHeight: string }>;
  weights: Record<string, number>;
}

export interface Spacing {
  base: string;
  scale: number[];
}

export interface Elevation {
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export interface Motion {
  duration: { fast: string; base: string; slow: string };
  ease: { linear: string; in: string; out: string; 'in-out': string };
}

export interface AtomDefaults {
  defaultVariant?: string;
  defaultSize?: string;
  defaultRadius?: string;
  defaultShadow?: string;
  defaultPadding?: string;
  defaultWeight?: string;
  defaultFit?: string;
  defaultOrientation?: string;
  availableVariants?: string[];
  availableSizes?: string[];
}

export interface Narrative {
  visualTheme?: string;
  colorRoleNarrative?: Record<string, { role: string; purpose: string }>;
  dosAndDonts?: { dos?: string[]; donts?: string[] };
  agentPromptGuide?: string;
}

export interface I18nConfig {
  defaultLocale: string;
  supportedLocales: string[];
  direction: 'ltr' | 'rtl';
  fontSubstitutions?: Record<string, string>;
}
